import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { STATUS, SUPPLY, PRIORITY, RISK } from "../constants";
import { useVoiceInput } from "../useVoice";
import api from "../api/client";
import Icon from "./Icon";

// Segmented-control style tab button (pill on a recessed track — see TabTrack).
export function TabButton({ active, onClick, children, badge }) {
  return (
    <button type="button" onClick={onClick}
      // Segmented control (§4): la opcion activa es fondo blanco + sombra
      // sutil, sin relieve ni anillo. El texto activo toma el acento del
      // modulo via `--acento`, que publica el layout segun la ruta.
      className={`relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${active ? "bg-white shadow-soft" : "text-tinta-2 hover:bg-white/60 hover:text-tinta"}`}
      style={active ? { color: "var(--acento, #1e4d8f)" } : undefined}>
      {children}
      {badge > 0 && (
        <span className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-tag px-1.5 text-[11px] font-bold ${active ? "text-white" : "bg-relleno text-tinta-2"}`}
          style={active ? { backgroundColor: "var(--acento, #1e4d8f)" } : undefined}>
          {badge}
        </span>
      )}
    </button>
  );
}

// Recessed "track" wrapper that holds a row of TabButton pills.
export function TabTrack({ children, className = "" }) {
  return (
    <div className={`-mx-5 mb-4 flex gap-1 overflow-x-auto rounded-lg bg-relleno p-1 ${className}`}>
      {children}
    </div>
  );
}

// Records a voice note and transcribes it server-side via Whisper (works in any
// browser with a mic, unlike live dictation which is Chrome/Edge only).
export function VoiceNoteButton({ onText, title = "Grabar nota de voz" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "nota.webm");
          const { data } = await api.post("/media/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text) onText(data.text);
        } catch { /* surfaced by caller toasts elsewhere */ }
        finally { setBusy(false); }
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
    } catch { /* mic denied */ }
  };
  const stop = () => { try { mrRef.current?.stop(); } catch { /* */ } setRecording(false); };

  return (
    <button type="button" title={title} onClick={recording ? stop : start} disabled={busy}
      className={`flex shrink-0 items-center gap-1 rounded-lg border px-2 py-2 text-sm ${recording ? "animate-pulse border-rose-300 bg-rose-50 text-rose-600" : "border-slate-300 text-slate-500 hover:bg-slate-100"}`}>
      {busy ? <span className="text-xs">…</span> : recording ? <span className="text-xs font-semibold">■ Detener</span> : <Icon name="mic" size={16} strokeWidth={2.2} />}
    </button>
  );
}

export function RiskBadge({ risk, compact = false }) {
  const r = RISK[risk] || RISK.low;
  if (compact) return <span className="inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ background: r.bar }} title={r.label} />;
  return <span className={`chip ${r.color}`}><span className="mr-1 inline-block h-1.5 w-1.5 rounded-full" style={{ background: r.bar }} />{r.label}</span>;
}

// Microphone button that dictates into a field via the Web Speech API.
export function MicButton({ onText, title = "Dictar por voz" }) {
  const { listening, toggle, supported } = useVoiceInput(onText);
  if (!supported) return null;
  return (
    <button type="button" onClick={toggle} title={title}
      className={`flex shrink-0 items-center gap-1 rounded-lg border px-2 py-2 text-sm ${listening ? "animate-pulse border-rose-300 bg-rose-50 text-rose-600" : "border-slate-300 text-slate-500 hover:bg-slate-100"}`}>
      {listening ? <span className="text-xs font-semibold">● Dictando</span> : <Icon name="mic" size={16} strokeWidth={2.2} />}
    </button>
  );
}

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.not_started;
  return (
    <span className={`chip ${s.color}`}>
      <span className={`mr-1 h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// Estado a mostrar en una tarea: si es de suministro (stage en SUPPLY), muestra
// el estado de suministro; si no, el estado normal. Úsalo en lista/tablero/ficha.
export function TaskStatusBadge({ task }) {
  const sup = task?.stage && SUPPLY[task.stage];
  if (sup) return (
    <span className={`chip ${sup.color}`}>
      <span className={`mr-1 h-1.5 w-1.5 rounded-full ${sup.dot}`} />
      {sup.label}
    </span>
  );
  return <StatusBadge status={task?.status} />;
}

export function PriorityBadge({ priority }) {
  const p = PRIORITY[priority] || PRIORITY.medium;
  return <span className={`chip ${p.color}`}>{p.label}</span>;
}

export function ProgressBar({ value = 0 }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
      <div
        className="h-full rounded-full bg-brand-500 transition-all"
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  );
}

// Presets de avatar: nombre de icono SVG (registrado en Icon.jsx) + color de
// fondo. Reemplaza los emojis previos, que se veían inconsistentes entre
// sistemas y no encajaban con la línea "profesional/sobria" del sistema.
export const AVATAR_PRESETS = {
  corp_tie: { icon: "user", bg: "#6B8FD6", label: "Ejecutivo" },
  corp_briefcase: { icon: "briefcase", bg: "#8E92DC", label: "Negocios" },
  corp_chart: { icon: "chart", bg: "#0ea5e9", label: "Analista" },
  corp_rocket: { icon: "zap", bg: "#A99BE0", label: "Líder" },
  corp_target: { icon: "target", bg: "#ec4899", label: "Estratega" },
  corp_globe: { icon: "globe", bg: "#14b8a6", label: "Global" },
  corp_crown: { icon: "crown", bg: "#E8B65A", label: "Director" },
  corp_star: { icon: "star", bg: "#E4788A", label: "Destacado" },
  tech_laptop: { icon: "laptop", bg: "#8E92DC", label: "Developer" },
  tech_robot: { icon: "cpu", bg: "#A99BE0", label: "AI/Bot" },
  tech_code: { icon: "edit", bg: "#5FBF9A", label: "Coder" },
  tech_cloud: { icon: "cloud", bg: "#0ea5e9", label: "Cloud" },
  tech_shield: { icon: "shield", bg: "#E4788A", label: "Security" },
  tech_gear: { icon: "cpu", bg: "#64748b", label: "SysAdmin" },
  tech_terminal: { icon: "terminal", bg: "#1e293b", label: "Terminal" },
  tech_bug: { icon: "bug", bg: "#F0A878", label: "QA/Debug" },
  tech_fire: { icon: "zap", bg: "#E4788A", label: "DevOps" },
  tech_brain: { icon: "brain", bg: "#a855f7", label: "Architect" },
  tech_network: { icon: "link", bg: "#0284c7", label: "Network" },
  tech_db: { icon: "database", bg: "#5FBF9A", label: "DBA" },
};

export function Avatar({ name = "?", size = 28, src = "" }) {
  if (src && src.startsWith("preset:")) {
    const key = src.replace("preset:", "").split("?")[0];
    const p = AVATAR_PRESETS[key];
    if (p) {
      return (
        <span className="inline-flex shrink-0 items-center justify-center rounded-full text-white"
          style={{ width: size, height: size, backgroundColor: p.bg }}
          title={name}>
          <Icon name={p.icon} size={Math.max(12, size * 0.55)} strokeWidth={2} />
        </span>
      );
    }
  }
  if (src && !src.startsWith("preset:")) {
    return (
      <img
        src={src}
        alt={name}
        title={name}
        className="inline-block shrink-0 rounded-full object-cover ring-1 ring-black/5"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, backgroundColor: `hsl(${hue} 55% 45%)`, fontSize: size * 0.4 }}
      title={name}
    >
      {initials || "?"}
    </span>
  );
}

// Acento por modulo para el tag del encabezado del modal. Repite el de la
// pagina a proposito: el documento pide "tag de color de modulo (igual que en
// la pagina)".
const TONO_MODAL = {
  tareas: ["bg-tareas-bg", "text-tareas", "bg-tareas", "Tareas"],
  proyectos: ["bg-proyectos-bg", "text-proyectos", "bg-proyectos", "Proyectos"],
  soporte: ["bg-soporte-bg", "text-soporte", "bg-soporte", "Soporte"],
  itam: ["bg-activos-bg", "text-activos", "bg-activos", "Activos"],
  reportes: ["bg-reportes-bg", "text-reportes", "bg-reportes", "Reportes"],
  herramientas: ["bg-herramientas-bg", "text-herramientas", "bg-herramientas", "Herramientas"],
  compras: ["bg-compras-bg", "text-compras", "bg-compras", "Compras"],
  tiendas: ["bg-tiendas-bg", "text-tiendas", "bg-tiendas", "Tiendas"],
};

export function Modal({
  open, onClose, children, title, wide, xl, confirmOnBackdrop, closeOnBackdrop,
  // --- Nuevo en el sistema de diseño 2026-09-18 (todo opcional, las llamadas
  // existentes siguen funcionando igual) ---
  tone,          // clave de modulo -> pinta el tag del encabezado
  paso,          // nº de paso actual (1-based); si falta, no hay indicador
  pasos,         // total de pasos
  pasoTitulo,    // nombre del paso ("Detalles")
  footer,        // contenido del pie fijo (botones)
}) {
  if (!open) return null;
  // El documento pide 80% del ancho y 80% del alto para el modal de trabajo.
  // Se aplica a `xl` (crear/editar tarea, proyecto, ticket…), que es el que
  // el documento describe: el que lleva pasos y campos de formulario.
  //
  // Los modales de confirmacion (`wide` y el tamaño por omision) NO se llevan
  // al 80%: un "¿Eliminar?" de dos lineas ocupando 80% de la pantalla es peor
  // usabilidad, no mejor. Es la unica desviacion deliberada de la seccion 7.
  const mw = xl
    ? "sm:w-[80vw] sm:max-w-[80vw] max-w-full"
    : wide ? "max-w-3xl" : "max-w-lg";
  const mh = xl ? "sm:h-[80vh] max-h-[92vh]" : "max-h-[92vh]";

  // Pedido explícito 2026-08-13: un clic accidental en el fondo oscuro (al
  // copiar texto, por ejemplo) no debe poder tirar un formulario a medio
  // llenar. Por eso el DEFAULT es que un clic afuera NO cierre nada, solo la
  // X — hay que pasar `closeOnBackdrop` a propósito en los modales realmente
  // inofensivos (un visor de solo lectura, un selector de filtro) para
  // recuperar el cierre por clic-afuera. `confirmOnBackdrop` sigue existiendo
  // para los pocos casos intermedios que quieran preguntar en vez de bloquear.
  const handleBackdrop = () => {
    if (!closeOnBackdrop && !confirmOnBackdrop) return;
    if (confirmOnBackdrop && !window.confirm("¿Cerrar sin guardar? Se perderán los cambios no guardados.")) return;
    onClose();
  };

  const [tagBg, tagTxt, barra, etiqueta] = TONO_MODAL[tone] || [];
  const conPasos = Number(pasos) > 1 && Number(paso) >= 1;

  return createPortal(
    // Overlay: vidrio esmerilado (no un velo plano) — rgba(24,24,27,.45) + blur.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(24,24,27,0.45)] p-4 backdrop-blur-[4px] sm:p-6"
      onClick={handleBackdrop}
    >
      <div
        className={`flex w-full flex-col rounded-xl border border-linea bg-superficie shadow-modal ${mw} ${mh} my-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="shrink-0 border-b border-linea px-5 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2.5">
                {etiqueta && <span className={`tag-modulo ${tagBg} ${tagTxt}`}>{etiqueta}</span>}
                {/* Título sólido, 18px/700. Sin degradado. */}
                <h3 className="truncate text-[18px] font-bold text-tinta">{title}</h3>
              </div>
              <button onClick={onClose} className="btn-ghost shrink-0 px-2 py-1" aria-label="Cerrar">
                <Icon name="x" size={16} />
              </button>
            </div>
            {conPasos && (
              // Indicador de progreso por pasos: etiqueta + barra fina de 3px
              // con el tramo activo en el color del módulo.
              <div className="mt-2">
                <div className="mb-1 text-[11px] font-semibold text-tinta-2">
                  Paso {paso} de {pasos}{pasoTitulo ? ` · ${pasoTitulo}` : ""}
                </div>
                <div className="h-[3px] w-full overflow-hidden rounded-tag bg-relleno">
                  <div
                    className={`h-full rounded-tag transition-all duration-300 ${barra || "bg-brand-600"}`}
                    style={{ width: `${Math.min(100, (Number(paso) / Number(pasos)) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          // Pie fijo, alineado a la derecha.
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-linea px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Uniform empty state: optional icon + title + hint + action button.
// Backward-compatible: <EmptyState>texto</EmptyState> sigue funcionando.
export function EmptyState({ icon, title, hint, action, children }) {
  return (
    <div className="flex flex-col items-center px-4 py-12 text-center">
      {icon && <Icon name={icon} size={30} className="text-slate-300" />}
      {title && <p className="mt-3 text-sm font-medium text-slate-500">{title}</p>}
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
      {children && <div className="mt-1 text-sm text-slate-400">{children}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function Spinner({ size = 18, className = "" }) {
  return (
    <svg className={`animate-spin text-brand-500 ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeOpacity="0.2" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function LoadingBlock({ label = "Cargando…", className = "py-10" }) {
  return (
    <div className={`flex items-center justify-center gap-2 text-sm text-slate-400 ${className}`} role="status">
      <Spinner /> {label}
    </div>
  );
}
